import React, { useState, useEffect } from 'react';
import { Download, Monitor, Apple, Loader2 } from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';
import './DownloadButton.css';

const DownloadButton = ({ variant = 'hero', showBothPlatforms = false }) => {
    const [os, setOs] = useState('windows');
    const [loading, setLoading] = useState(false);
    const [version, setVersion] = useState(null);

    useEffect(() => {
        // Detect user's operating system
        const platform = navigator.platform.toLowerCase();
        if (platform.includes('mac')) {
            setOs('mac');
        } else {
            setOs('windows');
        }

        // Fetch latest version info
        fetchLatestVersion();
    }, []);

    const fetchLatestVersion = async () => {
        try {
            const { data } = await supabase
                .from('app_versions')
                .select('version, download_url_windows, download_url_mac')
                .eq('is_latest', true)
                .single();

            if (data) {
                setVersion(data);
            }
        } catch (error) {
            console.error('Error fetching version:', error);
        }
    };

    const handleDownload = async () => {
        // Simple passcode protection
        const passcode = prompt("Please enter the download passcode:");
        if (passcode !== "revitron2026") {
            alert("Incorrect passcode.");
            return;
        }

        setLoading(true);

        try {
            // Get download URL from database
            let downloadUrl = os === 'mac'
                ? version?.download_url_mac
                : version?.download_url_windows;

            // If no URL in database, try direct Supabase storage
            if (!downloadUrl) {
                const filename = os === 'mac' ? 'Revitrone.dmg' : 'RevitroneSetup.exe';
                const { data } = supabase.storage
                    .from('releases')
                    .getPublicUrl(filename);

                if (data?.publicUrl) {
                    downloadUrl = data.publicUrl;
                }
            }

            if (downloadUrl) {
                // Direct download - opens in new tab triggering download
                window.location.href = downloadUrl;
            } else {
                alert('Download not available yet. Please check back soon!');
            }
        } catch (error) {
            console.error('Download error:', error);
            alert('Download failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Hero variant - large prominent button
    if (variant === 'hero') {
        return (
            <div className="downloadButton">
                <button
                    className="downloadButton__main"
                    onClick={handleDownload}
                    disabled={loading}
                >
                    {loading ? (
                        <Loader2 className="downloadButton__icon spin" />
                    ) : (
                        <Download className="downloadButton__icon" />
                    )}
                    <span className="downloadButton__text">
                        Download for {os === 'mac' ? 'Mac' : 'Windows'}
                    </span>
                    {version && (
                        <span className="downloadButton__version">v{version.version}</span>
                    )}
                </button>

                {showBothPlatforms && (
                    <button
                        className="downloadButton__alt"
                        onClick={handleDownload}
                    >
                        {os === 'mac' ? (
                            <>
                                <Monitor size={16} />
                                <span>Also available for Windows</span>
                            </>
                        ) : (
                            <>
                                <Apple size={16} />
                                <span>Also available for Mac</span>
                            </>
                        )}
                    </button>
                )}
            </div>
        );
    }

    // Compact variant
    return (
        <button
            className="downloadButton__compact"
            onClick={handleDownload}
            disabled={loading}
        >
            {loading ? (
                <Loader2 size={16} className="spin" />
            ) : (
                <Download size={16} />
            )}
            <span>Download</span>
        </button>
    );
};

export default DownloadButton;
